import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Field } from "./Field";
import type { ItemDetail } from "../lib/api";
import {
  DPI, canBluetooth, canSerial, connectBluetooth, connectPrintServer, connectSerial, connectedPrinter, reconnect, tsplJob,
  type PrinterConnection,
} from "../lib/labelPrinter";

/**
 * The item label: 2" × 1" for the Jadens JD-268BT thermal printer, laid out
 * like the labels already on the stock — the entry code, its Code 128
 * barcode, then a short brand/description on the left and the size on the
 * right.
 *
 * It is drawn once, dot for dot at the printer's 203 dpi, on a canvas. The
 * preview shows those exact dots, Print sends them straight to the printer
 * (lib/labelPrinter.ts), and the print-dialog fallback prints the same image.
 */

const LABEL_W = 2; // inches across the head
const LABEL_H = 1; // inches along the feed
const DOTS_W = LABEL_W * DPI; // 406
// Rows padded to 4 bytes (416 dots), exactly as the Windows driver sends them.
const CANVAS_W = Math.ceil(DOTS_W / 32) * 32;
const DOTS_H = LABEL_H * DPI; // 203
// A system font, so a label never waits on the web fonts to load.
const FONT = "Arial, Helvetica, sans-serif";
const pt = (points: number) => (points / 72) * DPI; // type size in dots
const at = (inches: number) => Math.round(inches * DPI); // a position in dots

/* --- Code 128 ------------------------------------------------------------- */

// Bar/space module widths for symbol values 0–106 (106 is the stop).
const PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];
const START_B = 104, START_C = 105, CODE_C = 99, STOP = 106;
const QUIET_ZONE = 10; // modules of white either side, per the spec

/**
 * Symbol values for `text`: set B, switching to set C for a trailing run of
 * digits so a SKU's number packs two digits per symbol — fewer, wider bars.
 */
function code128Values(text: string): number[] {
  const digits = /\d*$/.exec(text)?.[0] ?? "";
  const paired = digits.length >= 4 ? digits.length - (digits.length % 2) : 0;
  const head = text.slice(0, text.length - paired);
  const tail = text.slice(text.length - paired);
  const values: number[] = [];
  if (head) {
    values.push(START_B);
    for (const ch of head) {
      const c = ch.charCodeAt(0);
      values.push(c >= 32 && c <= 127 ? c - 32 : 31); // anything unencodable prints as "?"
    }
    if (tail) values.push(CODE_C);
  } else {
    values.push(START_C);
  }
  for (let i = 0; i < tail.length; i += 2) values.push(Number(tail.slice(i, i + 2)));
  // The start symbol and the first data symbol both carry weight 1.
  const check = values.reduce((sum, v, i) => sum + v * Math.max(i, 1), 0) % 103;
  return [...values, check, STOP];
}

/** Alternating bar/space widths in modules, start symbol through stop. */
function code128Runs(text: string): number[] {
  return code128Values(text).flatMap((v) => [...PATTERNS[v]].map(Number));
}

/* --- Drawing -------------------------------------------------------------- */

/** The largest size from `maxPx` down to `minPx` at which `text` fits `width`. */
function fitPx(ctx: CanvasRenderingContext2D, text: string, width: number, maxPx: number, minPx: number): number {
  ctx.font = `${maxPx}px ${FONT}`;
  const w = ctx.measureText(text).width;
  return w <= width ? maxPx : Math.max(minPx, (maxPx * width) / w);
}

/** `text` cut to `width` with an ellipsis, in the context's current font. */
function clip(ctx: CanvasRenderingContext2D, text: string, width: number): string {
  if (ctx.measureText(text).width <= width) return text;
  let t = text;
  while (t && ctx.measureText(`${t}…`).width > width) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

/**
 * Draw the label. Positions are measured off a photo of a label already on
 * the stock (T-001581): code capitals 0.19"–0.40", bars 0.46"–0.76", the
 * bottom line's baseline at 0.87".
 */
function drawLabel(canvas: HTMLCanvasElement, sku: string, line: string, size: string) {
  canvas.width = CANVAS_W;
  canvas.height = DOTS_H;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, CANVAS_W, DOTS_H);
  ctx.fillStyle = "#000";
  ctx.textBaseline = "alphabetic";

  ctx.font = `${fitPx(ctx, sku, DOTS_W - at(0.1), pt(21), pt(12))}px ${FONT}`;
  ctx.textAlign = "center";
  ctx.fillText(sku, DOTS_W / 2, at(0.4));

  // Every module a whole number of dots, so no bar is rounded thicker than its
  // neighbours: as wide as fits with a quiet zone either side, capped at 3
  // dots (a 1.5" code for a T-###### SKU, close to the existing labels).
  const runs = code128Runs(sku);
  const modules = runs.reduce((a, b) => a + b, 0);
  const moduleDots = Math.max(1, Math.min(3, Math.floor(DOTS_W / (modules + 2 * QUIET_ZONE))));
  const barDots = modules * moduleDots;
  const left = Math.floor((DOTS_W - barDots) / 2);
  let x = left;
  runs.forEach((w, i) => {
    if (i % 2 === 0) ctx.fillRect(x, at(0.46), w * moduleDots, at(0.3)); // even positions are bars
    x += w * moduleDots;
  });

  // The description and size run edge to edge with the bars.
  ctx.font = `${pt(8)}px ${FONT}`;
  const sizeWidth = size ? ctx.measureText(size).width + at(0.06) : 0;
  if (size) {
    ctx.textAlign = "right";
    ctx.fillText(size, left + barDots, at(0.87));
  }
  const room = barDots - sizeWidth;
  ctx.font = `${fitPx(ctx, line, room, pt(8), pt(6))}px ${FONT}`;
  ctx.textAlign = "left";
  ctx.fillText(clip(ctx, line, room), left, at(0.87));
}

/** The brand alone, in the label's capitals (the seller's choice: the title
 *  never fit and the size pill already says what the item is). Blank when the
 *  item has no brand; the field on the panel can still be typed into. */
function defaultLine(item: ItemDetail): string {
  return (item.brand ?? "").trim().toUpperCase();
}

/* --- Panel ---------------------------------------------------------------- */

type Route = "connected" | "bluetooth" | "bluetooth-all" | "serial" | "server";

/** The "Print label" panel on an item: word the label, check the preview, print. */
export function LabelPanel({ item, onClose }: { item: ItemDetail; onClose: () => void }) {
  const [line, setLine] = useState(() => defaultLine(item));
  const [size, setSize] = useState(() => (item.sizeLabel ?? "").toUpperCase());
  const [printer, setPrinter] = useState<PrinterConnection | null>(connectedPrinter);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [image, setImage] = useState(""); // the drawn label, for the dialog fallback
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const direct = canBluetooth || canSerial;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawLabel(canvas, item.sku, line.trim(), size.trim());
    setImage(canvas.toDataURL("image/png"));
  }, [item.sku, line, size]);

  // A printer allowed earlier in this browser reconnects without a picker.
  useEffect(() => {
    if (!printer) void reconnect().then((p) => { if (p) setPrinter(p); });
  }, []);

  async function print(route: Route) {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      // Connect first, straight off the tap: the browser only shows its
      // device picker in direct response to one.
      let p = route === "connected" ? connectedPrinter() : null;
      if (route === "server") p = connectPrintServer();
      if (!p) {
        p = route === "serial" ? await connectSerial() : await connectBluetooth(route === "bluetooth-all");
      }
      setPrinter(p);
      setStatus(`Sending to ${p.name}…`);
      await p.send(await tsplJob(canvasRef.current!, LABEL_W, LABEL_H));
      // Sent, not "printed": the printer doesn't report back.
      setStatus(p.via === "server" ? "Queued — the print server prints it within a few seconds." : `Sent to ${p.name}.`);
    } catch (err) {
      setStatus(null);
      // Closing the device picker isn't a failure.
      if (!(err instanceof DOMException && err.name === "NotFoundError")) {
        setError(err instanceof Error ? err.message : "Could not print");
      }
      setPrinter(connectedPrinter());
    } finally {
      setBusy(false);
    }
  }

  const scale = (96 * 1.6) / DPI; // on screen at 1.6× life size

  return (
    <section className="rail">
      <h2 className="eyebrow rail-label">Print label — 2" × 1"</h2>
      <div className="row">
        <Field label="Brand / description" hint="Shorten it to fit, e.g. CB KU JAYHAWKS">
          <input type="text" value={line} onChange={(e) => setLine(e.target.value.toUpperCase())} />
        </Field>
        <Field label="Size">
          <input type="text" value={size} onChange={(e) => setSize(e.target.value.toUpperCase())} />
        </Field>
      </div>

      {/* Cropped to the label: the padding dots past its right edge never print. */}
      <div className="label-preview" style={{ width: DOTS_W * scale, height: DOTS_H * scale }}>
        <canvas
          ref={canvasRef}
          className="label-canvas"
          style={{ width: CANVAS_W * scale, height: DOTS_H * scale }}
          aria-label={`Label preview for ${item.sku}`}
        />
      </div>

      {error && <div className="notice" style={{ marginTop: "0.75rem" }}>{error}</div>}
      {status && <div className="notice ok" style={{ marginTop: "0.75rem" }}>{status}</div>}

      {direct ? (
        <>
          <div className="actions">
            {printer ? (
              <button onClick={() => void print("connected")} disabled={busy}>
                {busy ? "Printing" : "Print"}
              </button>
            ) : (
              <>
                {canBluetooth && (
                  <button onClick={() => void print("bluetooth")} disabled={busy}>
                    {busy ? "Printing" : "Print via Bluetooth"}
                  </button>
                )}
                {canSerial && (
                  <button className={canBluetooth ? "ghost" : undefined} onClick={() => void print("serial")} disabled={busy}>
                    Print via COM port
                  </button>
                )}
              </>
            )}
            <button className="quiet" onClick={onClose} disabled={busy}>Close</button>
          </div>
          <div className="printer-row hint">
            <span>{printer ? `Connected: ${printer.name}` : "No printer connected yet — the first print asks which one."}</span>
            {printer && canBluetooth && (
              <button className="quiet" onClick={() => void print("bluetooth")} disabled={busy}>Switch printer</button>
            )}
            {canBluetooth && (
              <button className="quiet" onClick={() => void print("bluetooth-all")} disabled={busy}>
                Printer not listed? Show all Bluetooth devices
              </button>
            )}
            {printer?.via !== "server" && (
              <button className="quiet" onClick={() => void print("server")} disabled={busy}>
                Send to the print server instead
              </button>
            )}
            <button className="quiet" onClick={() => window.print()} disabled={busy}>Use the print dialog</button>
          </div>
          <p className="hint">
            Prints straight to the JD-268BT, no Jadens app and no dialog. Close the Jadens app first —
            the printer takes one connection at a time. On a computer, "COM port" uses the Bluetooth
            link Windows set up when the printer was paired. The print server is the little computer
            plugged into the printer; it prints whatever is sent to it, from any browser.
          </p>
        </>
      ) : (
        <>
          <div className="actions">
            <button onClick={() => void print("server")} disabled={busy}>
              {busy ? "Sending" : "Print"}
            </button>
            <button className="ghost" onClick={() => window.print()} disabled={busy}>Use the print dialog</button>
            <button className="quiet" onClick={onClose} disabled={busy}>Close</button>
          </div>
          <p className="hint">
            Print sends the label to the print server, the little computer plugged into the JD-268BT.
            It has to be on and online. This browser can't talk to the printer over Bluetooth itself;
            for that, use Bluefy on iPhone or Chrome/Edge on a computer.
          </p>
        </>
      )}

      {createPortal(
        <div className="print-label-root">
          <style>{`@page { size: ${LABEL_W}in ${LABEL_H}in; margin: 0; }`}</style>
          {image && <img className="print-label-img" src={image} alt="" />}
        </div>,
        document.body
      )}
    </section>
  );
}
